import type { HoverBoxConfig } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import { animated, useSpring } from "@react-spring/three";
import type { ThreeEvent } from "@react-three/fiber";
import { useMemo } from "react";
import { Vector3 } from "three";

const HOVER_BOX_HEIGHT_PADDING = 0.3;

export interface HoverBoxProps {
  hovered: boolean;
  config: HoverBoxConfig;
  modelBottomY?: number;
  modelHeight?: number;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
}

const HOVER_BOX_OPACITY_ON_HOVER = 0.5;

export const HoverBox = (props: HoverBoxProps) => {
  const { opacity } = useSpring({
    config: { duration: 300 },
    opacity: props.hovered ? HOVER_BOX_OPACITY_ON_HOVER : 0,
  });

  const effectiveHeight =
    props.modelHeight === undefined
      ? (props.config.scale?.[1] ?? 0)
      : props.modelHeight + HOVER_BOX_HEIGHT_PADDING;

  const { position, scale } = useMemo(
    () => ({
      position: new Vector3(
        props.config.position?.[0] ?? 0,
        props.config.position?.[1] ??
          (props.modelBottomY ?? 0) + effectiveHeight / 2,
        props.config.position?.[2] ?? 0
      ),
      scale: new Vector3(
        props.config.scale?.[0] ?? 0,
        effectiveHeight,
        props.config.scale?.[2] ?? 0
      ),
    }),
    [
      props.config.scale,
      props.config.position,
      props.modelBottomY,
      effectiveHeight,
    ]
  );

  return (
    <animated.mesh onClick={props.onClick} position={position} scale={scale}>
      <boxGeometry />
      <animated.meshBasicMaterial
        color="white"
        depthTest={false}
        opacity={opacity}
        transparent
      />
    </animated.mesh>
  );
};
